import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { useIsMounted } from '../hooks'
import { getRationing, shouldDebitMote } from '../identity'

export const FlightRecorderContext = createContext({})

const DEFAULT_STATE = {
  requests: {},
  finalized: false,
  moteId: null,
  chunksErrorCount: 0,
  chunksErrorThreshold: 50,
  shouldDebit: shouldDebitMote(),
  ...getRationing()
}

export const FlightRecorderProvider = ({ children }) => {
  const [state, setState] = useState(DEFAULT_STATE)
  const isMounted = useIsMounted()
  const resolveRef = useRef(null)
  const promiseRef = useRef(null)

  function registerRequest(route) {
    if (!isMounted()) return

    promiseRef.current = new Promise(resolve => (resolveRef.current = resolve))

    setState(prev => ({
      ...prev,
      requests: {
        ...prev.requests,
        [route]: 1
      }
    }))
  }

  function bumpRequestRetries(route) {
    if (!isMounted()) return
    setState(prev => ({
      ...prev,
      requests: {
        ...prev.requests,
        [route]: prev.requests[route] + 1
      }
    }))
  }

  function deregisterRequest(route) {
    if (!isMounted()) return
    setState(prev => {
      const requests = { ...prev.requests }

      delete requests[route]
      return {
        ...prev,
        requests
      }
    })
  }

  function increaseChunksErrorCount(route) {
    if (!isMounted()) return
    setState(prev => {
      const requests = { ...prev.requests }

      delete requests[route]
      return {
        ...prev,
        requests,
        chunksErrorCount: prev.chunksErrorCount + 1
      }
    })
  }

  function assignMoteId(moteId) {
    if (!isMounted()) return
    setState(prev => {
      const requests = { ...prev.requests }

      delete requests['m/register']
      return {
        ...prev,
        requests,
        moteId
      }
    })
  }

  function handleFinalizeSuccess() {
    if (!isMounted()) return
    setState(prev => ({ ...prev, finalized: true }))
  }

  function resetState() {
    checkRationing(DEFAULT_STATE)
  }

  function checkRationing(stateOverride = {}) {
    if (!isMounted()) return

    setState(prev => ({
      ...prev,
      ...stateOverride,
      shouldDebit: shouldDebitMote(),
      ...getRationing()
    }))
  }

  useEffect(() => {
    checkRationing()
  }, [])

  const chunksInFlight = Object.keys(state.requests).length > 0

  useEffect(() => {
    if (!chunksInFlight && resolveRef.current) {
      resolveRef.current(state.requests)
    }
  }, [chunksInFlight])

  return (
    <FlightRecorderContext.Provider
      value={{
        flightState: state,
        chunksInFlight,
        registerRequest,
        bumpRequestRetries,
        deregisterRequest,
        assignMoteId,
        handleFinalizeSuccess,
        increaseChunksErrorCount,
        checkRationing,
        resetFlightState: resetState,
        waitForEmptyChunks
      }}
    >
      {children}
    </FlightRecorderContext.Provider>
  )

  function waitForEmptyChunks() {
    return promiseRef.current
  }
}

export const useFlightRecorder = () => {
  return useContext(FlightRecorderContext)
}
